import { ViewWithLoader } from "@/components/v2/ViewWithLoader";
import { useTrans } from "@/translations";

import { useMemo } from "react";

import { max, min } from "lodash";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const TooltipContent = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { value: number }[];
  label?: string;
}) => {
  const { t } = useTrans();

  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="rounded-lg bg-[#1D1C1C] px-3 py-2 text-white">
      <div className="text-[13px] font-bold">{label}</div>
      <div className="text-[13px] opacity-85">
        {payload[0].value}%{" "}
        {t("contaminationPage:contaminationTrendTooltipText")}
      </div>
    </div>
  );
};

export type ContaminationTrendPoint = {
  month: string;
  rate: number;
};

type Props = {
  graphData: ContaminationTrendPoint[];
  isLoading: boolean;
};

export const ContaminationTrendGraph: React.FC<Props> = ({
  graphData,
  isLoading,
}) => {
  const maxYValue = useMemo(() => {
    const maxValue = max(graphData.map((d) => Number(d.rate)));

    if (!maxValue) return 0;

    return min([(Math.floor(maxValue / 10) + 1.5) * 10, 100]);
  }, [graphData]);

  return (
    <ViewWithLoader isLoading={isLoading}>
      <div className="mt-[13.2px] h-[257px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={graphData}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient
                id="contaminationTrendFill"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#FF8736" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#FF8736" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="#EEE7E7" />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tick={{ fill: "#6F6C6C", fontSize: 10.5 }}
              dy={8}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: "#B2ABAB", fontSize: 10 }}
              tickFormatter={(value: number) => `${value}%`}
              width={36}
              domain={[0, maxYValue]}
            />
            <Tooltip content={<TooltipContent />} />
            <Area
              type="monotone"
              dataKey="rate"
              stroke="#FF8736"
              strokeWidth={2.5}
              fill="url(#contaminationTrendFill)"
              dot={{
                r: 3.5,
                fill: "#fff",
                stroke: "#FF8736",
                strokeWidth: 2,
              }}
              activeDot={{
                r: 4.5,
                fill: "#fff",
                stroke: "#FF8736",
                strokeWidth: 2,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </ViewWithLoader>
  );
};
